// ============================================================================
// TEMPLATE PREVIEW COMPONENT
// Display template details with phases and closing actions summary
// ============================================================================

import React, { useState } from 'react';
import {
  ChevronDown,
  ChevronRight,
  Layers,
  Clock,
  FileText,
  Target,
  CheckCircle
} from 'lucide-react';
import { SessionTemplate, PhaseCategory } from '../../types';

// Simplified phase type that works with both full PhaseTemplate and snapshot phases
type PreviewPhase = {
  phaseTemplateId: string;
  versionNumber?: number;
  name: string;
  description: string;
  duration: string;
  purpose: string;
  goal: string;
  successCriteria: string[];
  category: PhaseCategory;
  orderIndex?: number;
};

interface TemplatePreviewProps {
  template: SessionTemplate;
  phaseTemplates: PreviewPhase[];
}

export default function TemplatePreview({ template, phaseTemplates }: TemplatePreviewProps) {
  const [expandedPhases, setExpandedPhases] = useState<Set<string>>(new Set());

  const togglePhase = (phaseId: string) => {
    const newExpanded = new Set(expandedPhases);
    if (newExpanded.has(phaseId)) {
      newExpanded.delete(phaseId);
    } else {
      newExpanded.add(phaseId);
    }
    setExpandedPhases(newExpanded);
  };

  const getPhaseTemplate = (phaseId: string): PreviewPhase | undefined => {
    return phaseTemplates.find(p => p.phaseTemplateId === phaseId);
  };

  // Calculate estimated duration from phase durations (e.g., "30 min" -> 30)
  const totalDuration = template.phaseReferences.reduce((sum, ref) => {
    const phase = getPhaseTemplate(ref.phaseTemplateId);
    if (!phase) return sum;
    const match = phase.duration.match(/(\d+)/);
    const minutes = match ? parseInt(match[1], 10) : 0;
    return sum + minutes;
  }, 0);

  const getCategoryLabel = (cat: string) => {
    const labels: Record<string, string> = {
      WORKSHOP: 'Workshop',
      BRAINSTORM: 'Brainstorm',
      REVIEW: 'Review',
      PLANNING: 'Planning',
      CUSTOM: 'Custom'
    };
    return labels[cat] || cat;
  };

  const getScopeLabel = (scope: string) => {
    const labels: Record<string, string> = {
      SESSION: 'Session',
      USER: 'Personal',
      ORGANIZATION: 'Organization',
      SYSTEM: 'System'
    };
    return labels[scope] || scope;
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Template Overview Card */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-primary/10 rounded-lg">
            <FileText className="w-5 h-5 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-text-main">Template Overview</h3>
            <p className="text-sm text-text-sub">Core details and classification.</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-primary/10 text-primary">
              {getCategoryLabel(template.category)}
            </span>
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-slate-100 text-slate-600">
              {getScopeLabel(template.scope)}
            </span>
          </div>
        </div>

        <p className="text-sm text-text-main leading-relaxed mb-5">
          {template.description}
        </p>

        {/* Stats Row */}
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-lg">
            <Layers className="w-4 h-4 text-slate-500" />
            <span className="text-sm font-medium text-text-main">
              {template.phaseReferences.length} phase{template.phaseReferences.length !== 1 ? 's' : ''}
            </span>
          </div>
          {totalDuration > 0 && (
            <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-lg">
              <Clock className="w-4 h-4 text-slate-500" />
              <span className="text-sm font-medium text-text-main">
                ~{totalDuration} min estimated
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Purpose & Goal Card */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Target className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-text-main">Purpose & Goal</h3>
            <p className="text-sm text-text-sub">What this template aims to achieve.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <p className="text-xs font-medium text-text-sub uppercase tracking-wider mb-1.5">Purpose</p>
            <p className="text-sm text-text-main leading-relaxed">{template.purpose}</p>
          </div>
          <div>
            <p className="text-xs font-medium text-text-sub uppercase tracking-wider mb-1.5">Goal</p>
            <p className="text-sm text-text-main leading-relaxed">{template.goal}</p>
          </div>
        </div>
      </div>

      {/* Phases Card */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Layers className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-text-main">Workshop Phases</h3>
              <p className="text-sm text-text-sub">Session flow in sequential order.</p>
            </div>
          </div>
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-primary/10 text-primary">
            {template.phaseReferences.length} phase{template.phaseReferences.length !== 1 ? 's' : ''}
          </span>
        </div>

        <div className="space-y-2">
          {template.phaseReferences.map((ref, index) => {
            const phase = getPhaseTemplate(ref.phaseTemplateId);
            const isExpanded = expandedPhases.has(ref.phaseTemplateId);

            if (!phase) {
              return (
                <div
                  key={ref.phaseTemplateId}
                  className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-600"
                >
                  Phase not found: {ref.phaseTemplateId}
                </div>
              );
            }

            return (
              <div
                key={ref.phaseTemplateId}
                className="rounded-lg border border-slate-200 overflow-hidden"
              >
                {/* Phase Header */}
                <button
                  onClick={() => togglePhase(ref.phaseTemplateId)}
                  className="w-full px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors flex items-center justify-between text-left"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-7 h-7 rounded-md bg-primary text-white flex items-center justify-center font-bold text-xs flex-shrink-0">
                      {index + 1}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-text-main">
                        {phase.name}
                      </p>
                      {phase.duration && (
                        <p className="text-xs text-text-sub">{phase.duration}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-600">
                      {phase.category}
                    </span>
                    {isExpanded ? (
                      <ChevronDown className="w-4 h-4 text-slate-400" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-slate-400" />
                    )}
                  </div>
                </button>

                {/* Phase Details */}
                {isExpanded && (
                  <div className="px-4 py-4 border-t border-slate-200 bg-white space-y-4">
                    <div>
                      <p className="text-xs font-medium text-text-sub uppercase tracking-wider mb-1">
                        Purpose
                      </p>
                      <p className="text-sm text-text-main leading-relaxed">
                        {phase.purpose}
                      </p>
                    </div>

                    {phase.successCriteria && phase.successCriteria.length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-text-sub uppercase tracking-wider mb-2">
                          Success Criteria
                        </p>
                        <ul className="space-y-1.5">
                          {phase.successCriteria.map((criterion, i) => (
                            <li key={`${criterion}-${i}`} className="flex items-start gap-2">
                              <CheckCircle className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                              <span className="text-sm text-text-main">{criterion}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
